// Customer-visible widget strings, keyed flat with dot-namespacing.
// Each entry has an `en` and `ja` variant. JA is the runtime default when
// `window.MonogatariLocale === "ja"` (set by sections/monogatari-uploader.liquid
// from `request.locale.iso_code`); otherwise EN renders.
//
// Tone: 敬語/丁寧 polite-formal commerce register. Matches the theme strings in
// docs/copy/UI_TRANSLATIONS_JA.md. Customer is addressed as お客様; products
// referred to with honorific お/ご (お写真, ご注文, お支払い).
//
// Placeholders use {brace} syntax — the t() helper does literal substitution.

export type Locale = "en" | "ja";

export interface I18nEntry {
  en: string;
  ja: string;
}

export const STRINGS: Record<string, I18nEntry> = {
  // ── App.tsx ────────────────────────────────────────────────────
  "app.missing_style.title": {
    en: "Pick a template to begin",
    ja: "テンプレートをお選びください",
  },
  "app.missing_style.body": {
    en: "This page doesn't have a template selected. Browse our collection and click any portrait to start customizing it.",
    ja: "このページにはテンプレートが設定されていません。コレクションから肖像画をお選びいただき、カスタマイズを開始してください。",
  },
  "app.missing_style.cta": {
    en: "Browse all templates →",
    ja: "すべてのテンプレートを見る →",
  },
  "app.quota.first": {
    en: "Your first {n} reveals are free · No credit card",
    ja: "最初の{n}回は無料・クレジットカード不要",
  },
  "app.quota.left.one": {
    en: "1 free reveal left",
    ja: "残り無料プレビュー 1回",
  },
  "app.quota.left.many": {
    en: "{n} free reveals left",
    ja: "残り無料プレビュー {n}回",
  },

  // ── SingleFormStep.tsx ─────────────────────────────────────────
  "upload.tap": {
    en: "Tap to add photo",
    ja: "タップしてお写真を追加",
  },
  "upload.hint": {
    en: "From camera or gallery · JPG / PNG / WEBP up to 12MB",
    ja: "カメラまたはアルバムから・JPG / PNG / WEBP・最大12MB",
  },
  "upload.crop.hint": {
    en: "Drag to reposition · pinch / slide to zoom · square crop centers his face",
    ja: "ドラッグで位置調整・ピンチ / スライドでズーム・お顔が中央に来るように正方形に切り抜き",
  },
  "upload.crop.use_original": {
    en: "Use original",
    ja: "元のお写真を使用",
  },
  "upload.crop.cropping": {
    en: "Cropping…",
    ja: "切り抜き中…",
  },
  "upload.crop.use_cropped": {
    en: "✓ Use cropped",
    ja: "✓ 切り抜きを使用",
  },
  "upload.zoom_label": {
    en: "Zoom",
    ja: "ズーム",
  },
  "upload.thumb_alt": {
    en: "Selected photo",
    ja: "選択したお写真",
  },
  "upload.photo_ready": {
    en: "Photo ready",
    ja: "お写真の準備完了",
  },
  "upload.change_photo": {
    en: "Change photo",
    ja: "お写真を変更",
  },
  "upload.pick_another": {
    en: "Pick a different photo",
    ja: "別のお写真を選ぶ",
  },
  "upload.submit": {
    en: "Generate Preview →",
    ja: "プレビューを作成 →",
  },
  "upload.error.too_big": {
    en: "That file is over 12MB. Try a smaller photo.",
    ja: "ファイルサイズが12MBを超えています。より小さいお写真でお試しください。",
  },
  "upload.error.bad_type": {
    en: "Use a JPG, PNG, WEBP, or HEIC photo.",
    ja: "JPG・PNG・WEBP・HEIC形式のお写真をご利用ください。",
  },
  "upload.error.crop_failed": {
    en: "Couldn't crop the photo. Try Use original instead.",
    ja: "切り抜きに失敗しました。「元のお写真を使用」をお試しください。",
  },
  "upload.error.confirm_crop": {
    en: "Confirm or skip the crop first.",
    ja: "先に切り抜きを確定するかスキップしてください。",
  },
  "upload.error.no_photo": {
    en: "Please upload a photo.",
    ja: "お写真をアップロードしてください。",
  },

  // ── LoadingStep.tsx (widget mode only renders headlines) ───────
  "loading.percent": {
    en: "{pct}% complete",
    ja: "進行状況 {pct}%",
  },
  "loading.hero_fallback": {
    en: "the hero",
    ja: "お孫さま",
  },
  "loading.stage_1": {
    en: "Reading {name}'s expression…",
    ja: "{name}のお顔を読み取り中…",
  },
  "loading.stage_2": {
    en: "Generating the storybook scene for {name}…",
    ja: "{name}のための物語のシーンを生成しています…",
  },
  "loading.stage_3": {
    en: "Adding cinematic light…",
    ja: "光と色彩を加えています…",
  },
  "loading.stage_4": {
    en: "Almost ready — final touches…",
    ja: "もうすぐ完成・最後の仕上げ中…",
  },
  "loading.stage_5": {
    en: "Adding the final touches — masterpieces take a moment…",
    ja: "高画質に仕上げています・もう少しお待ちください…",
  },

  // ── EmailGate.tsx ──────────────────────────────────────────────
  "email_gate.title": {
    en: "Unlock",
    ja: "続きを解放" ,
  },
  "email_gate.body": {
    en: "You've used your free reveals. Drop your email and we'll send {name}'s reading + unlock {bonus} more transformations.",
    ja: "無料プレビューの回数を使い切りました。メールアドレスをご登録いただくと、{name}の肖像画をお送りし、追加で{bonus}回ご利用いただけます。",
  },
  "email_gate.placeholder": {
    en: "you@email.com",
    ja: "you@email.com",
  },
  "email_gate.submitting": {
    en: "Saving…",
    ja: "保存中…",
  },
  "email_gate.submit": {
    en: "Continue — unlock {bonus} more",
    ja: "続行・追加{bonus}回を解放",
  },
  "email_gate.skip": {
    en: "Maybe later",
    ja: "後で",
  },
  "email_gate.error": {
    en: "Couldn't save your email — try again.",
    ja: "メールアドレスの保存に失敗しました。もう一度お試しください。",
  },

  // ── PaywallStep.tsx ────────────────────────────────────────────
  "paywall.title": {
    en: "Sold Out",
    ja: "無料プレビュー終了",
  },
  "paywall.body": {
    en: "You've experienced every free transformation we offer. Add {name}'s portrait to your cart to keep the collection going — every purchase unlocks more variations.",
    ja: "無料プレビューをすべてご利用いただきました。{name}の肖像画をカートに追加すると、さらに多くのバリエーションをご利用いただけます。",
  },
  "paywall.checkout": {
    en: "See on products",
    ja: "商品ページへ",
  },
  "paywall.restart": {
    en: "Start over with another photo",
    ja: "別のお写真でやり直す",
  },

  // ── PreviewStep.tsx — signature block ──────────────────────────
  "preview.sig.toggle_off": {
    en: "Add a signature (free)",
    ja: "サインを追加(無料)",
  },
  "preview.sig.toggle_on": {
    en: "Signature added",
    ja: "サインを追加済み",
  },
  "preview.sig.text_label": {
    en: "Text on canvas",
    ja: "キャンバスに入れる文字",
  },
  "preview.sig.placeholder": {
    en: "e.g. お孫さまのお名前",
    ja: "例:お孫さまのお名前",
  },
  "preview.sig.size_label": {
    en: "Size",
    ja: "サイズ",
  },
  "preview.sig.size_aria": {
    en: "Signature size",
    ja: "サインのサイズ",
  },
  "preview.sig.reset_title": {
    en: "Reset to 100%",
    ja: "100%にリセット",
  },
  "preview.sig.hint": {
    en: "Appears in the bottom-right. Drag the slider to resize, click a font to switch.",
    ja: "右下に表示されます。スライダーでサイズ調整、フォントをクリックで切り替えられます。",
  },
  "preview.sig.font.honor": {
    en: "Honor",
    ja: "オナー(優雅)",
  },
  "preview.sig.font.handwritten": {
    en: "Handwritten",
    ja: "手書き風",
  },
  "preview.sig.font.marker": {
    en: "Marker",
    ja: "マーカー",
  },

  // ── PreviewStep.tsx — format picker ────────────────────────────
  "preview.format.from": {
    en: "from ¥{price}",
    ja: "¥{price}から",
  },
  "preview.format.frame_color": {
    en: "Frame Color",
    ja: "額装の色",
  },
  "preview.format.size": {
    en: "Size",
    ja: "サイズ",
  },

  // ── PreviewStep.tsx — checkout bar (standalone mode) ───────────
  "preview.shipping.free": {
    en: "Free shipping",
    ja: "送料無料",
  },
  "preview.shipping.flat": {
    en: "¥350 shipping",
    ja: "送料 ¥350",
  },
  "preview.add_to_cart": {
    en: "Add to Cart",
    ja: "カートに追加",
  },
  "preview.adding_to_cart": {
    en: "Adding to cart…",
    ja: "カートに追加中…",
  },
  "preview.view_cart": {
    en: "🛒 View cart ({n}) →",
    ja: "🛒 カートを見る({n}) →",
  },
  "preview.start_over": {
    en: "Start over with another photo",
    ja: "別のお写真でやり直す",
  },
  "preview.try_another": {
    en: "🔄 Try another photo",
    ja: "🔄 別のお写真で試す",
  },

  // ── PreviewStep.tsx — order-confirm overlay ────────────────────
  "cart.added_title": {
    en: "Added to cart",
    ja: "カートに追加しました",
  },
  "cart.empty_title": {
    en: "Your cart is empty",
    ja: "カートは空です",
  },
  "cart.line.collapse": {
    en: "Collapse",
    ja: "閉じる",
  },
  "cart.line.expand": {
    en: "Click to view larger",
    ja: "クリックで拡大表示",
  },
  "cart.line.qty_dec": {
    en: "Decrease quantity",
    ja: "数量を減らす",
  },
  "cart.line.qty_inc": {
    en: "Increase quantity",
    ja: "数量を増やす",
  },
  "cart.line.remove": {
    en: "Remove",
    ja: "削除",
  },
  "cart.line.remove_aria": {
    en: "Remove {code}",
    ja: "{code}を削除",
  },
  "cart.discount.placeholder": {
    en: "Discount code",
    ja: "クーポンコード",
  },
  "cart.discount.apply": {
    en: "Apply",
    ja: "適用",
  },
  "cart.totals.subtotal.one": {
    en: "Subtotal (1 item)",
    ja: "小計(1点)",
  },
  "cart.totals.subtotal.many": {
    en: "Subtotal ({n} items)",
    ja: "小計({n}点)",
  },
  "cart.totals.discount": {
    en: "Discount",
    ja: "割引",
  },
  "cart.totals.discount_with_code": {
    en: "Discount ({code})",
    ja: "割引({code})",
  },
  "cart.totals.total": {
    en: "Total",
    ja: "合計",
  },
  "cart.totals.shipping_note": {
    en: "Free shipping · taxes calculated at checkout",
    ja: "送料無料・税込価格はお会計時に計算されます",
  },
  "cart.proceed_checkout": {
    en: "Proceed to secure checkout →",
    ja: "安全なお会計へ進む →",
  },
  // Sibling-discount nudge above the checkout button. Intentionally EMPTY
  // by default — it must only render once the owner has actually created
  // the matching automatic discount in Shopify admin. Activated by filling
  // the "Sibling discount line" field on the Monogatari Uploader section
  // (Theme Editor), which overrides this via window.MonogatariStrings.
  "cart.sibling_deal": {
    en: "",
    ja: "",
  },
  "cart.keep_customizing": {
    en: "← Keep customizing",
    ja: "← カスタマイズに戻る",
  },

  // ── products.ts catalog ────────────────────────────────────────
  // Each product variant has 4 customer-visible fields: label / subtitle /
  // material / features (array). Size labels stay as-is (numeric + unit).
  "product.photo_panel.label": {
    en: "Glass Photo Frame",
    ja: "ガラスフォトフレーム",
  },
  "product.photo_panel.subtitle": {
    en: "Tabletop sublimation glass photo frame",
    ja: "卓上サイズの昇華転写ガラスフォトフレーム",
  },
  "product.photo_panel.material": {
    en: "Sublimation-ready glass, high-gloss photo finish",
    ja: "昇華転写対応ガラス製、高光沢フォト仕上げ",
  },
  "product.photo_panel.feature_1": {
    en: "230×180mm tabletop display size",
    ja: "230×180mm の卓上ディスプレイサイズ",
  },
  "product.photo_panel.feature_2": {
    en: "Sublimation-printed photographic finish — sharp & vibrant",
    ja: "昇華転写印刷による写真仕上げ — シャープで鮮やか",
  },
  "product.photo_panel.feature_3": {
    en: "5 mm glass — sits on a desk, shelf, or display cabinet",
    ja: "厚さ5mmのガラス — デスク・棚・飾り棚にそのまま設置",
  },
  "product.photo_panel.feature_4": {
    en: "Easel stand included for upright display",
    ja: "立てかけ用のイーゼルスタンド付き",
  },
  "product.photo_panel.feature_5": {
    en: "Gift-box ready — ideal for grandparents → grandchildren",
    ja: "ギフトボックス付き — 祖父母さまからお孫さまへの贈りものに",
  },

  "product.gallery_canvas.label": {
    en: "Gallery Canvas",
    ja: "ギャラリーキャンバス",
  },
  "product.gallery_canvas.subtitle": {
    en: "Stretched, 0.75\" depth",
    ja: "張りキャンバス・厚み0.75インチ",
  },
  "product.gallery_canvas.material": {
    en: "Cotton-polyester canvas composite (333 g/m²) on radiata pine stretcher bars",
    ja: "綿・ポリエステル混紡キャンバス(333 g/m²)、ラジアータパイン木枠張り",
  },
  "product.gallery_canvas.feature_1": {
    en: "0.75\" gallery-wrap depth",
    ja: "0.75インチのギャラリーラップ厚",
  },
  "product.gallery_canvas.feature_2": {
    en: "Proprietary matte coating for vibrant prints",
    ja: "独自のマットコーティングで鮮やかな印刷",
  },
  "product.gallery_canvas.feature_3": {
    en: "Back hanging hardware included",
    ja: "壁掛け金具付き",
  },
  "product.gallery_canvas.feature_4": {
    en: "Eco-friendly FSC-certified frame",
    ja: "FSC認証木材を使用した環境配慮型フレーム",
  },

  "product.framed_canvas.label": {
    en: "Framed Canvas",
    ja: "額装キャンバス",
  },
  "product.framed_canvas.subtitle": {
    en: "Pinewood frame, multiple colors",
    ja: "パイン材の額装、複数色からお選びいただけます",
  },
  "product.framed_canvas.material": {
    en: "Cotton-polyester canvas with proprietary coating in a pinewood frame",
    ja: "独自コーティング処理した綿・ポリエステル混紡キャンバス、パイン材の額装入り",
  },
  "product.framed_canvas.feature_1": {
    en: "Pinewood frame (2 color options)",
    ja: "パイン材の額装(2色からお選びいただけます)",
  },
  "product.framed_canvas.feature_2": {
    en: "Special proprietary coating for lasting colors",
    ja: "独自コーティングで色あせを防止",
  },
  "product.framed_canvas.feature_3": {
    en: "Sawtooth hanging hardware pre-attached",
    ja: "鋸歯型壁掛け金具を取り付け済み",
  },
  "product.framed_canvas.feature_4": {
    en: "FSC-certified sustainable materials",
    ja: "FSC認証の持続可能な素材を使用",
  },

  // Frame colour names — JP convention: 英名(日本語名) or just 日本語名
  "product.color.black": { en: "Black", ja: "ブラック" },
  "product.color.walnut": { en: "Walnut", ja: "ウォルナット" },
  "product.color.white": { en: "White", ja: "ホワイト" },
  "product.color.natural": { en: "Natural", ja: "ナチュラル" },
  "product.color.dark_brown": { en: "Dark Brown", ja: "ダークブラウン" },
};
